
import { toast as sonnerToast } from "sonner";

// Create custom wrappers for Sonner toast functions to maintain compatibility
// with components expecting the previous API pattern

interface ToastOptions {
  title?: string;
  description?: string;
  variant?: "default" | "destructive";
  [key: string]: any;
}

// Custom toast function that adapts to sonner's API with error handling
const toast = (message: string | ToastOptions) => {
  try {
    if (typeof message === "string") {
      return sonnerToast(message);
    }
    
    const { title, description, variant, ...rest } = message;
    
    if (variant === "destructive") {
      return sonnerToast.error(title || "Error", {
        description,
        ...rest
      });
    }
    
    return sonnerToast(title || "", {
      description,
      ...rest
    });
  } catch (error) {
    console.warn('Toast error, falling back to console:', error, message);
    // Fallback to console logging if toast fails
    if (typeof message === "string") {
      console.log('Toast:', message);
    } else {
      console.log('Toast:', message.title || 'Notification', message.description || '');
    }
  }
};

// Add the methods from sonner to our custom toast with support for both formats and error handling
toast.success = (message: string | ToastOptions, options?: any) => {
  try {
    if (typeof message === "string") {
      return sonnerToast.success(message, options);
    }
    
    const { title, description, ...rest } = message;
    return sonnerToast.success(title || "Success", {
      description,
      ...rest,
      ...options
    });
  } catch (error) {
    console.warn('Success toast error:', error);
    console.log('Success:', typeof message === "string" ? message : message.title);
  }
};

toast.error = (message: string | ToastOptions, options?: any) => {
  try {
    if (typeof message === "string") {
      return sonnerToast.error(message, options);
    }
    
    const { title, description, ...rest } = message;
    return sonnerToast.error(title || "Error", {
      description,
      ...rest,
      ...options
    });
  } catch (error) {
    console.warn('Error toast error:', error);
    console.error('Error:', typeof message === "string" ? message : message.title);
  }
};

toast.info = (message: string | ToastOptions, options?: any) => {
  try {
    if (typeof message === "string") {
      return sonnerToast.info(message, options);
    }
    
    const { title, description, ...rest } = message;
    return sonnerToast.info(title || "Info", {
      description,
      ...rest,
      ...options
    });
  } catch (error) {
    console.warn('Info toast error:', error);
    console.log('Info:', typeof message === "string" ? message : message.title);
  }
};

toast.warning = (message: string | ToastOptions, options?: any) => {
  try {
    if (typeof message === "string") {
      return sonnerToast.warning(message, options);
    }
    
    const { title, description, ...rest } = message;
    return sonnerToast.warning(title || "Warning", {
      description,
      ...rest,
      ...options
    });
  } catch (error) {
    console.warn('Warning toast error:', error);
    console.warn('Warning:', typeof message === "string" ? message : message.title);
  }
};

// Safe dismiss function
toast.dismiss = (...args: any[]) => {
  try {
    return sonnerToast.dismiss(...args);
  } catch (error) {
    console.warn('Toast dismiss error:', error);
  }
};

// Create a useToast hook to maintain compatibility with existing code
export const useToast = () => {
  return { toast };
};

export { toast };
